import { useState, useEffect, useCallback } from "react";
import { PageHeader, SectionCard, Skeleton } from "@/components/Primitives";
import { fmtMoney, fmtDateTime, fmtDate } from "@/lib/format";
import api, { downloadBlob } from "@/lib/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { RefreshCw, Download, RotateCcw } from "lucide-react";
import ZoneReports from "@/components/reports/ZoneReports";

const PERIODS = [
  ["today", "Today"], ["yesterday", "Yesterday"], ["this_week", "This Week"], ["last_week", "Last Week"],
  ["this_month", "This Month"], ["last_month", "Last Month"], ["this_quarter", "This Quarter"],
  ["this_year", "This Year"], ["all", "All Time"], ["custom", "Custom Range"],
];
const COLORS = ["#00C9E0", "#9ACC32", "#FE3967", "#FE421C", "#A78BFA", "#38BDF8", "#F59E0B"];

const KPI_DEFS = [
  ["total_leads", "Total Leads", false, "uncontacted_leads"], ["new_leads", "New Leads", false],
  ["leads_contacted", "Leads Contacted", false], ["uncontacted_leads", "Uncontacted", false, "uncontacted_leads"],
  ["followups_due", "Follow-Ups Due", false], ["overdue_followups", "Overdue Follow-Ups", false, "overdue_followups"],
  ["calls", "Calls", false], ["visits", "Visits", false], ["meetings", "Meetings", false],
  ["opportunities", "Open Opportunities", false, "pipeline"], ["pipeline_value", "Pipeline Value", true, "pipeline"],
  ["sales_won", "Sales Won", false, "won_sales"], ["sales_lost", "Sales Lost", false],
  ["sales_revenue", "Sales Revenue", true, "won_sales"], ["new_advertisers", "New Advertisers", false],
  ["active_advertisers", "Active Advertisers", false], ["renewals_due", "Renewals Due", false, "renewals"],
  ["renewals_completed", "Renewals Completed", false], ["contracts_expiring", "Contracts Expiring", false],
  ["commissions_pending", "Commissions Pending", true], ["commissions_approved", "Commissions Approved", true],
  ["commissions_paid", "Commissions Paid", true],
];

export default function Reports() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const isManager = can("tenant_owner", "platform_owner", "sales_manager");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("this_month");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [filters, setFilters] = useState({});
  const [opts, setOpts] = useState({ users: [], territories: [], zones: [], products: [] });
  const [drill, setDrill] = useState(null);
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "overview";
  const setTab = (v) => setSp({ tab: v }, { replace: true });
  const zonesTab = tab === "zones";

  useEffect(() => {
    Promise.all([api.get("/users"), api.get("/territories"), api.get("/zones"), api.get("/products")])
      .then(([u, t, z, p]) => setOpts({ users: u.data.items, territories: t.data.items, zones: z.data.items, products: p.data.items }));
  }, []);

  const query = useCallback(() => {
    const p = new URLSearchParams({ period });
    if (period === "custom") { p.set("start", custom.start); p.set("end", custom.end); }
    Object.entries(filters).forEach(([k, v]) => v && p.set(k, v));
    return p.toString();
  }, [period, custom, filters]);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/reports/overview?${query()}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [query]);
  useEffect(() => { load(); }, [load]);

  const openDrill = async (metric, extra = {}) => {
    const p = new URLSearchParams({ metric, ...Object.fromEntries(new URLSearchParams(query())), ...extra });
    const { data: d } = await api.get(`/reports/drill?${p}`);
    setDrill({ metric, title: extra.title || metric, items: d.items });
  };
  const exportCsv = (section) => downloadBlob(`/reports/export?section=${section}&${query()}`, `report_${section}.csv`);
  const reset = () => { setFilters({}); setPeriod("this_month"); };

  const k = data?.kpis || {};
  const setF = (key, v) => setFilters((f) => ({ ...f, [key]: v === "__all" ? "" : v }));

  return (
    <div data-testid="page-reports">
      <PageHeader title="Reports" subtitle="Live management overview from current CRM records.">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-slate-400" data-testid="report-updated">{data ? `Updated ${fmtDateTime(data.updated_at)}` : ""}</span>
          <Button size="sm" variant="outline" onClick={load} data-testid="report-refresh"><RefreshCw className="h-4 w-4" />Refresh</Button>
        </div>
      </PageHeader>

      <div className={`flex flex-col lg:flex-row lg:items-end gap-3 mb-5 flex-wrap ${zonesTab ? "hidden" : ""}`}>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-full lg:w-44" data-testid="report-period"><SelectValue /></SelectTrigger>
          <SelectContent>{PERIODS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        {period === "custom" && (
          <div className="flex gap-2">
            <Input type="date" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} data-testid="report-start" />
            <Input type="date" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} data-testid="report-end" />
          </div>
        )}
        <FSelect label="Salesperson" value={filters.salesperson} onChange={(v) => setF("salesperson", v)} items={opts.users.map((u) => [u.id, u.name])} testid="report-f-salesperson" />
        <FSelect label="Market" value={filters.territory} onChange={(v) => setF("territory", v)} items={opts.territories.map((t) => [t.name, t.name])} testid="report-f-territory" />
        <FSelect label="Zone" value={filters.zone} onChange={(v) => setF("zone", v)} items={opts.zones.map((z) => [z.name, z.name])} testid="report-f-zone" />
        <FSelect label="Product" value={filters.product} onChange={(v) => setF("product", v)} items={opts.products.map((p) => [p.name, p.name])} testid="report-f-product" />
        <Button size="sm" variant="outline" onClick={reset} data-testid="report-reset"><RotateCcw className="h-4 w-4" />Reset</Button>
        <span className="ml-auto text-sm font-semibold text-[#0094A8]" data-testid="report-period-label">{data?.period?.label}</span>
      </div>

      {loading || !data ? (
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
      ) : (
        <>
          <div className={`grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 mb-6 ${zonesTab ? "hidden" : ""}`}>
            {KPI_DEFS.map(([key, label, money, drillMetric]) => (
              <button key={key} onClick={drillMetric ? () => openDrill(drillMetric, { title: label }) : undefined}
                className={`cc-card p-3.5 text-left ${drillMetric ? "hover:border-[#00C9E0]/50 hover:shadow-sm cursor-pointer" : ""}`} data-testid={`kpi-${key}`}>
                <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
                <p className="mt-1.5 font-display text-xl font-bold text-[#1E2025] tnum">{money ? fmtMoney(k[key]) : (k[key] ?? 0)}</p>
              </button>
            ))}
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="flex-wrap h-auto">
              {isManager && <TabsTrigger value="staff-review" data-testid="rtab-staff-review">Staff Review</TabsTrigger>}
              <TabsTrigger value="overview" data-testid="rtab-overview">Overview</TabsTrigger>
              {isManager && <TabsTrigger value="leads-report" data-testid="rtab-leads">Leads</TabsTrigger>}
              <TabsTrigger value="staff" data-testid="rtab-staff">Staff Performance</TabsTrigger>
              <TabsTrigger value="advertisers" data-testid="rtab-advertisers">Advertisers & Renewals</TabsTrigger>
              <TabsTrigger value="zones" data-testid="rtab-zones">Zone Reports</TabsTrigger>
              <TabsTrigger value="commissions" data-testid="rtab-commissions">Commissions</TabsTrigger>
            </TabsList>

            {isManager && (
              <TabsContent value="staff-review" className="mt-4">
                <StaffReview period={period} custom={custom} users={opts.users} setDrill={setDrill} />
              </TabsContent>
            )}

            {isManager && (
              <TabsContent value="leads-report" className="mt-4">
                <LeadsReport period={period} custom={custom} users={opts.users} setDrill={setDrill} />
              </TabsContent>
            )}

            <TabsContent value="overview" className="mt-4 space-y-4">
              <div className="grid lg:grid-cols-2 gap-4">
                <Chart title="Pipeline Value by Stage" rows={data.pipeline_by_stage} money onBar={(r) => openDrill("pipeline", { stage: r.label, title: `Pipeline · ${r.label}` })} />
                <Chart title="Sales Revenue by Product" rows={data.sales_by_product} money action={<Button size="sm" variant="outline" onClick={() => exportCsv("sales_by_product")} data-testid="export-sales"><Download className="h-4 w-4" />CSV</Button>} />
                <Chart title="Leads by Source" rows={data.leads_by_source} />
                <SectionCard title="Activity">
                  <div className="grid grid-cols-3 gap-3">
                    {Object.entries(data.activity).map(([key, v]) => (
                      <div key={key} className="rounded-lg bg-slate-50 p-3"><p className="text-[10px] uppercase text-slate-400 font-semibold">{key.replace(/_/g, " ")}</p><p className="font-display text-lg font-bold tnum">{v}</p></div>
                    ))}
                  </div>
                </SectionCard>
              </div>
            </TabsContent>

            <TabsContent value="staff" className="mt-4">
              <SectionCard title="Staff Performance" action={<Button size="sm" variant="outline" onClick={() => exportCsv("staff")} data-testid="export-staff"><Download className="h-4 w-4" />Export CSV</Button>}>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[900px]">
                    <thead><tr className="border-b bg-slate-50/70 text-left text-[11px] uppercase tracking-wide text-slate-500">
                      {["Salesperson", "Leads", "Contacted", "Calls", "Visits", "Mtgs", "Opps", "Won", "Sales", "Revenue", "Conv %", "Pipeline", "Advertisers", "Comm Pending"].map((h) => <th key={h} className="px-3 py-2 whitespace-nowrap">{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {data.staff.map((s) => (
                        <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setDrill({ staff: s, title: s.name })} data-testid={`staff-row-${s.id}`}>
                          <td className="px-3 py-2.5 font-semibold text-slate-800">{s.name}</td>
                          <td className="px-3 py-2.5 tnum">{s.leads_assigned}</td><td className="px-3 py-2.5 tnum">{s.leads_contacted}</td>
                          <td className="px-3 py-2.5 tnum">{s.calls}</td><td className="px-3 py-2.5 tnum">{s.visits}</td><td className="px-3 py-2.5 tnum">{s.meetings}</td>
                          <td className="px-3 py-2.5 tnum">{s.opps_created}</td><td className="px-3 py-2.5 tnum">{s.opps_won}</td><td className="px-3 py-2.5 tnum">{s.sales_count}</td>
                          <td className="px-3 py-2.5 tnum font-semibold text-[#0094A8]">{fmtMoney(s.revenue)}</td><td className="px-3 py-2.5 tnum">{s.conversion}%</td>
                          <td className="px-3 py-2.5 tnum">{fmtMoney(s.pipeline_value)}</td><td className="px-3 py-2.5 tnum">{s.advertisers}</td><td className="px-3 py-2.5 tnum">{fmtMoney(s.commission_pending)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
            </TabsContent>

            <TabsContent value="advertisers" className="mt-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <SectionCard title="Advertiser Health">
                  {["green", "yellow", "red"].map((lv) => (
                    <button key={lv} onClick={() => openDrill("advertiser_health", { level: lv, title: `${lv} advertisers` })} className="w-full flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0 hover:text-[#0094A8]" data-testid={`health-${lv}`}>
                      <span className="flex items-center gap-2 capitalize"><span className="h-2.5 w-2.5 rounded-full" style={{ background: lv === "green" ? "#9ACC32" : lv === "yellow" ? "#F59E0B" : "#FE421C" }} />{lv}</span>
                      <span className="tnum font-bold">{data.advertiser_health[lv]}</span>
                    </button>
                  ))}
                </SectionCard>
                <SectionCard title="Renewals">
                  {Object.entries(data.renewals).map(([g, v]) => (
                    <button key={g} onClick={() => openDrill("renewals", { group: g, title: `Renewals · ${g}` })} className="w-full flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0 hover:text-[#0094A8]" data-testid={`renewal-${g}`}>
                      <span className="capitalize">{g.replace(/_/g, " ")}</span><span className="tnum font-bold">{v}</span>
                    </button>
                  ))}
                </SectionCard>
              </div>
            </TabsContent>

            <TabsContent value="zones" className="mt-4">
              <ZoneReports />
            </TabsContent>

            <TabsContent value="commissions" className="mt-4">
              <div className="grid grid-cols-3 gap-4">
                {[["pending", "Pending", "#F59E0B"], ["approved", "Approved", "#00C9E0"], ["paid", "Paid", "#9ACC32"]].map(([key, label, c]) => (
                  <div key={key} className="cc-card p-5" data-testid={`comm-${key}`}><p className="cc-overline" style={{ color: c }}>{label}</p><p className="mt-2 font-display text-2xl font-bold tnum">{fmtMoney(data.commissions[key])}</p></div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}

      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="report-drill-dialog">
          <DialogHeader><DialogTitle className="font-display capitalize">{drill?.title}</DialogTitle></DialogHeader>
          {drill?.staff ? (
            <div className="grid grid-cols-2 gap-3 text-sm">
              {Object.entries({ "Leads Assigned": drill.staff.leads_assigned, "Leads Contacted": drill.staff.leads_contacted, "Uncontacted": drill.staff.uncontacted, "Calls": drill.staff.calls, "Visits": drill.staff.visits, "Meetings": drill.staff.meetings, "Monthly Contacts": drill.staff.monthly_contacts, "Follow-Ups Overdue": drill.staff.followups_overdue, "Opps Created": drill.staff.opps_created, "Opps Won": drill.staff.opps_won, "Opps Lost": drill.staff.opps_lost, "Sales": drill.staff.sales_count, "Revenue": fmtMoney(drill.staff.revenue), "Conversion": drill.staff.conversion + "%", "Avg Deal": fmtMoney(drill.staff.avg_deal), "Advertisers": drill.staff.advertisers, "Commission Pending": fmtMoney(drill.staff.commission_pending), "Commission Paid": fmtMoney(drill.staff.commission_paid), "Last Activity": drill.staff.last_activity ? fmtDate(drill.staff.last_activity) : "—" }).map(([lbl, v]) => (
                <div key={lbl} className="rounded-lg bg-slate-50 p-2.5"><p className="text-[10px] uppercase text-slate-400 font-semibold">{lbl}</p><p className="font-semibold tnum">{v}</p></div>
              ))}
            </div>
          ) : drill?.items ? (
            drill.items.length === 0 ? <p className="text-sm text-slate-400 py-6 text-center">No records.</p> : (
              <div className="divide-y divide-slate-100">
                {drill.items.map((it) => (
                  <button key={it.id} onClick={() => { setDrill(null); navigate(it.link); }} className="w-full text-left py-2.5 hover:bg-slate-50 px-1 rounded" data-testid={`drill-item-${it.id}`}>
                    <p className="text-sm font-medium text-slate-800">{it.label}</p><p className="text-xs text-slate-400">{it.sub}</p>
                  </button>
                ))}
              </div>
            )
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LeadsReport({ period, custom, users, setDrill }) {
  const [sel, setSel] = useState("");
  const [d, setD] = useState(null);
  const q = useCallback(() => {
    const p = new URLSearchParams({ period });
    if (period === "custom") { p.set("start", custom.start); p.set("end", custom.end); }
    if (sel) p.set("salesperson", sel);
    return p.toString();
  }, [period, custom, sel]);
  useEffect(() => { api.get(`/reports/leads-overview?${q()}`).then((r) => setD(r.data)); }, [q]);
  const drill = async (metric, title) => {
    const p = new URLSearchParams({ metric, period });
    if (period === "custom") { p.set("start", custom.start); p.set("end", custom.end); }
    if (sel) p.set("salesperson", sel);
    const { data } = await api.get(`/reports/drill?${p}`);
    setDrill({ metric, title, items: data.items });
  };
  if (!d) return <Skeleton className="h-64" />;
  const Breakdown = ({ title, obj }) => (
    <SectionCard title={title}>
      {Object.keys(obj || {}).length === 0 ? <p className="text-sm text-slate-400">No data.</p> : (
        <div className="space-y-1.5">{Object.entries(obj).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
          <div key={k} className="flex items-center justify-between text-sm"><span className="text-slate-700 truncate">{k}</span><span className="tnum font-semibold text-slate-500">{v}</span></div>
        ))}</div>
      )}
    </SectionCard>
  );
  const KPIS = [["total", "Total Leads", "total_leads"], ["won", "Won", "won_sales"], ["lost", "Lost", "lost_opportunities"], ["nurture", "Nurture", null], ["followups_due", "Follow-Ups Due", "followups_due"], ["overdue", "Overdue", "overdue_followups"], ["no_followup", "No Follow-Up", null]];
  return (
    <div className="space-y-4" data-testid="leads-report">
      <Select value={sel || "__all"} onValueChange={(v) => setSel(v === "__all" ? "" : v)}>
        <SelectTrigger className="w-full sm:w-72" data-testid="leads-report-staff"><SelectValue placeholder="All Sales Staff" /></SelectTrigger>
        <SelectContent><SelectItem value="__all">All Sales Staff</SelectItem>{(users || []).filter((u) => ["sales_rep", "sales_manager", "tenant_owner"].includes(u.role)).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
      </Select>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {KPIS.map(([k, label, m]) => (
          <button key={k} disabled={!m} onClick={() => m && drill(m, label)} className={`cc-card p-3 text-left ${m ? "hover:border-[#00C9E0]/50 cursor-pointer" : "cursor-default"}`} data-testid={`lr-${k}`}>
            <p className="text-[10px] font-bold uppercase text-slate-400">{label}</p>
            <p className="font-display text-xl font-bold tnum">{d[k] ?? 0}</p>
          </button>
        ))}
        <div className="cc-card p-3" data-testid="lr-est_pipeline"><p className="text-[10px] font-bold uppercase text-slate-400">Est. Pipeline</p><p className="font-display text-xl font-bold tnum text-[#0094A8]">{fmtMoney(d.est_pipeline_value)}</p></div>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <Breakdown title="Leads by Pipeline Stage" obj={d.by_stage} />
        <Breakdown title="Hot / Warm / Cold" obj={d.by_temperature} />
        <Breakdown title="Leads by Source" obj={d.by_source} />
        <Breakdown title="Lost Reasons" obj={d.by_lost_reason} />
        <Breakdown title="Average Days in Stage" obj={d.avg_time_in_stage} />
      </div>
    </div>
  );
}

function StaffReview({ period, custom, users, setDrill }) {
  const navigate = useNavigate();
  const [sel, setSel] = useState("");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const activeUsers = (users || []).filter((u) => u.active !== false && ["sales_rep", "sales_manager", "tenant_owner"].includes(u.role));

  const qstr = useCallback(() => {
    const p = new URLSearchParams({ period });
    if (period === "custom") { p.set("start", custom.start); p.set("end", custom.end); }
    if (sel) p.set("salesperson", sel);
    return p.toString();
  }, [period, custom, sel]);

  useEffect(() => {
    setLoading(true);
    api.get(`/reports/staff-dashboard?${qstr()}`).then((r) => setD(r.data)).finally(() => setLoading(false));
  }, [qstr]);

  const DRILL = {
    total_leads: "total_leads", new_leads: "new_leads", active_leads: "active_leads",
    followups_due: "followups_due", overdue_followups: "overdue_followups",
    total_opportunities: "all_opportunities", open_opportunities: "pipeline",
    won: "won_sales", lost: "lost_opportunities", appointments: "appointments",
    meetings: "meetings", calls: "calls", tasks: "tasks",
    completed_activities: "completed_activities", host_businesses: "host_businesses", advertisers: "advertisers",
  };
  const CARDS = [
    ["total_leads", "Total Leads"], ["new_leads", "New Leads"], ["active_leads", "Active Leads"],
    ["followups_due", "Follow-Ups Due"], ["overdue_followups", "Overdue Follow-Ups"],
    ["total_opportunities", "Total Opportunities"], ["open_opportunities", "Open Opportunities"],
    ["won", "Won Sales"], ["lost", "Lost Opportunities"], ["appointments", "Appointments"],
    ["meetings", "Meetings"], ["calls", "Calls"], ["tasks", "Open Tasks"],
    ["completed_activities", "Completed Activities"], ["host_businesses", "Host Businesses"], ["advertisers", "Advertisers"],
  ];
  const openDrill = async (key, label) => {
    const p = new URLSearchParams({ metric: DRILL[key], period });
    if (period === "custom") { p.set("start", custom.start); p.set("end", custom.end); }
    if (sel) p.set("salesperson", sel);
    const { data } = await api.get(`/reports/drill?${p}`);
    setDrill({ metric: DRILL[key], title: label, items: data.items });
  };
  const c = d?.counts || {};

  return (
    <div className="space-y-4" data-testid="staff-review">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <Select value={sel || "__all"} onValueChange={(v) => setSel(v === "__all" ? "" : v)}>
          <SelectTrigger className="w-full sm:w-72" data-testid="staff-review-select"><SelectValue placeholder="Select Staff Member" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All Sales Staff</SelectItem>
            {activeUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {sel && d?.territories && (
          <div className="flex flex-wrap items-center gap-1.5" data-testid="staff-review-territories">
            <span className="text-xs text-slate-400">Territories:</span>
            {d.territories.length ? d.territories.map((t) => <span key={t} className="rounded-full bg-[#9ACC32]/15 text-[#5B880C] px-2 py-0.5 text-[11px] font-semibold">{t}</span>) : <span className="text-xs text-slate-400">None assigned</span>}
          </div>
        )}
      </div>

      {loading || !d ? (
        <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
      ) : (
        <>
          <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
            {CARDS.map(([key, label]) => (
              <button key={key} onClick={() => openDrill(key, `${label}${sel ? "" : " · All Staff"}`)}
                className="cc-card p-3.5 text-left hover:border-[#00C9E0]/50 hover:shadow-sm cursor-pointer" data-testid={`sr-kpi-${key}`}>
                <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
                <p className="mt-1.5 font-display text-xl font-bold text-[#1E2025] tnum">{c[key] ?? 0}</p>
              </button>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <SectionCard title="Calendar / Schedule">
              {(!d.calendar || d.calendar.length === 0) ? <p className="text-sm text-slate-400 py-6 text-center">No scheduled items in this period.</p> : (
                <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
                  {d.calendar.map((e) => (
                    <button key={`${e.kind}-${e.id}`} onClick={() => navigate(e.business_id ? `/businesses/${e.business_id}` : (e.kind === "task" ? "/tasks" : "/"))}
                      className="w-full text-left flex items-center gap-3 py-2.5 px-1 hover:bg-slate-50 rounded" data-testid={`sr-cal-${e.id}`}>
                      <span className={`h-2 w-2 rounded-full shrink-0 ${e.kind === "task" ? "bg-[#FE421C]" : "bg-[#00C9E0]"}`} />
                      <div className="min-w-0 flex-1"><p className="text-sm font-medium text-slate-800 truncate">{e.title}</p><p className="text-xs text-slate-400">{e.kind === "task" ? "Task" : "Appointment"} · {(e.when || "").slice(0, 16).replace("T", " ")}{e.status ? ` · ${e.status}` : ""}</p></div>
                    </button>
                  ))}
                </div>
              )}
            </SectionCard>
            <SectionCard title="Recent Activity">
              {(!d.recent_activity || d.recent_activity.length === 0) ? <p className="text-sm text-slate-400 py-6 text-center">No recent activity.</p> : (
                <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
                  {d.recent_activity.map((a) => (
                    <button key={a.id} onClick={() => a.business_id && navigate(`/businesses/${a.business_id}`)}
                      className="w-full text-left py-2.5 px-1 hover:bg-slate-50 rounded" data-testid={`sr-act-${a.id}`}>
                      <p className="text-sm font-medium text-slate-800 truncate">{a.title}</p>
                      <p className="text-xs text-slate-400 capitalize">{(a.type || "activity")} · {fmtDateTime(a.at)}</p>
                    </button>
                  ))}
                </div>
              )}
            </SectionCard>
          </div>
        </>
      )}
    </div>
  );
}

function FSelect({ label, value, onChange, items, testid }) {
  return (
    <Select value={value || "__all"} onValueChange={onChange}>
      <SelectTrigger className="w-full lg:w-40" data-testid={testid}><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent><SelectItem value="__all">All {label}s</SelectItem>{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function Chart({ title, rows, money, action, onBar }) {
  return (
    <SectionCard title={title} action={action}>
      {!rows || rows.length === 0 ? <p className="text-sm text-slate-400 py-10 text-center">No data.</p> : (
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={rows} layout="vertical" margin={{ left: 10 }}>
            <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => money ? fmtMoney(v) : v} />
            <YAxis type="category" dataKey="label" width={120} tick={{ fontSize: 11 }} stroke="#94a3b8" />
            <Tooltip formatter={(v) => money ? fmtMoney(v) : v} contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} />
            <Bar dataKey="value" radius={[0, 6, 6, 0]} onClick={onBar} cursor={onBar ? "pointer" : "default"}>{rows.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </SectionCard>
  );
}
